import React, { useState } from 'react';
import { useCourseAccess } from '../hooks/useCourseAccess';
import { VideoPlayer } from '../components/VideoPlayer';
import { CourseAccessGuard } from '../components/CourseAccessGuard';
import { DashboardLayout } from '../components/DashboardLayout';
import { ModuleFiles } from '../components/ModuleFiles';
import { MODULES, Video, Module } from '../data/modules';

export const Modules: React.FC = () => {
  const { 
    getAvailableModulesByDate, 
    getModuleStatus,
    getAccessLevelName, 
    getAccessLevelDescription,
    courseAccess,
    userBatch,
    isPreviousBatch,
    isCurrentBatch
  } = useCourseAccess();
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  const availableModules = getAvailableModulesByDate();
  const allModules = MODULES.filter(module => {
    // Show all modules the user has access to, but mark unavailable ones as locked
    switch (courseAccess) {
      case 'Uni':
        return module.id <= 2;
      case 'First':
        return module.id <= 4;
      case 'Manager':
        return module.id <= 5;
      default:
        return false;
    }
  });

  return (
    <DashboardLayout>
      <div className="py-6 bg-[#FCFAF7] min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#203D32] font-alegreya mb-4">
              Course Modules
            </h1>
            
            {/* Batch-specific information */}
            <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#203D32]">{getAccessLevelName()}</h3>
                  {isPreviousBatch ? (
                    <p className="text-sm text-green-700">✅ You have lifetime access to all your modules</p>
                  ) : (
                    <p className="text-sm text-blue-700">📅 Modules will be released according to schedule</p>
                  )}
                </div>
                <span className="text-sm text-gray-500">
                  {availableModules.length} of {allModules.length} modules available
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Module List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-[#203D32] mb-4">
                  Module List
                </h3>
                <div className="space-y-3">
                  {allModules.map((module) => {
                    const status = getModuleStatus(module);
                    const isAvailable = status.status === 'available';
                    
                    return (
                      <div
                        key={module.id}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                          selectedModule?.id === module.id
                            ? 'border-[#203D32] bg-[#FCFAF7]'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${
                          !isAvailable ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                        onClick={() => {
                          if (isAvailable) {
                            setSelectedModule(module);
                            setSelectedVideo(module.videos[0]); // Select first video by default
                          }
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-[#203D32]">
                              Module {module.id}: {module.title}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {module.description}
                            </p>
                            <div className="flex items-center mt-2 space-x-4">
                              <div className="flex items-center">
                                <svg className="h-4 w-4 text-gray-400 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                <span className="text-xs text-gray-500">{module.videos.length} videos</span>
                              </div>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Video Player and Video List */}
            <div className="lg:col-span-2">
              {selectedModule ? (
                <div className="space-y-6">
                  {/* Video Player */}
                  {selectedVideo && (
                    <VideoPlayer
                      moduleNumber={selectedModule.id}
                      videoId={`module-${selectedModule.id}-video-${selectedVideo.id}`}
                      title={selectedVideo.title}
                      bunnyNetVideoId={selectedVideo.bunnyNetVideoId}
                      posterUrl={selectedVideo.posterUrl}
                    />
                  )}

                  {/* Video List */}
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-[#203D32] mb-4">
                      Videos in Module {selectedModule.id}
                    </h3>
                    <div className="space-y-3">
                      {selectedModule.videos.map((video) => (
                        <div
                          key={video.id}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                            selectedVideo?.id === video.id
                              ? 'border-[#203D32] bg-[#FCFAF7]'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedVideo(video)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-[#203D32]">
                                {video.title}
                              </h4>
                              
                              <div className="flex items-center mt-2">
                                <svg className="h-4 w-4 text-gray-400 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-xs text-gray-500">{video.duration}</span>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-[#203D32]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Module Files */}
                  <ModuleFiles moduleId={selectedModule.id} />
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Select a Module
                  </h3>
                  <p className="text-gray-500">
                    Choose a module from the list to start learning.
                  </p>
                </div>
              )}
            </div>
          </div>

        
        </div>
      </div>
    </DashboardLayout>
  );
}; 